// format contains help for the parser to decide correct date
// possible values:
// YMD, DMY, MDY
export function parseDate (dateStr: string, format: string): Date | null {
  if (!dateStr) return null;

  // ISO format (contains 'T'): take the date part as UTC midnight, ignoring the time.
  if (dateStr.includes('T')) {
    const [datePart] = dateStr.split('T');
    const [year, month, day] = datePart.split('-');
    return new Date(Date.UTC(+year, +month - 1, +day));
  }

  // Try the parser selected by the format hint. An unknown hint yields null and falls through.
  const byFormatHint: Record<string, (s: string) => Date | null> = {
    YMD: parseDateYmd,
    MDY: parseDateMdy,
    DMY: parseDateDmy
  };
  const parsedDate = byFormatHint[format]?.(dateStr) ?? null;
  if (parsedDate !== null) return parsedDate;

  // Otherwise fall back to the more general formats.
  return parseDateFallback(dateStr);
}

// Generic fallback parsing tried after the format-hinted parser fails: DD.MM.YYYY, DDMMYYYY,
// DD.MM.YYYY HH:mm[:ss[.SSS]], and finally the native Date constructor. First match wins.
function parseDateFallback (dateStr: string): Date | null {
  // DD.MM.YYYY
  if (/^\d{2}\.\d{2}\.\d{4}$/.test(dateStr)) {
    const [day, month, year] = dateStr.split('.');
    const date = new Date(Date.UTC(+year, +month - 1, +day));
    return isValidDate(date) ? date : null;
  }

  // DDMMYYYY
  if (/^\d{8}$/.test(dateStr)) {
    const day = dateStr.substring(0, 2);
    const month = dateStr.substring(2, 4);
    const year = dateStr.substring(4, 8);
    const date = new Date(Date.UTC(+year, +month - 1, +day));
    return isValidDate(date) ? date : null;
  }

  // DD.MM.YYYY HH:mm[:ss[.SSS]]
  if (/^\d{2}\.\d{2}\.\d{4}\s\d{2}:\d{2}(:\d{2})?(\.\d{1,3})?$/.test(dateStr)) {
    const [datePart, timePart] = dateStr.split(' ');
    const [day, month, year] = datePart.split('.');
    const timeComponents = timePart.split(/[:.]/);

    const date = new Date(Date.UTC(
      +year,
      +month - 1,
      +day,
      +timeComponents[0],
      +timeComponents[1],
      timeComponents[2] ? +timeComponents[2] : 0,
      timeComponents[3] ? +timeComponents[3] : 0,
    ));
    return isValidDate(date) ? date : null;
  }

  // Native Date constructor as a last resort.
  const date = new Date(dateStr);
  return isValidDate(date) ? date : null;
}


function parseDateDmy (dateStr: string): Date | null {
  const separator = findSeparator(dateStr);
  const parts = dateStr.split(separator);
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth();

  // First matching pattern wins (mirrors the former sequential if-chain). Each build closure keeps
  // the exact Date.UTC expression of the branch it replaced.
  return firstDatePatternMatch(dateStr, parts, [
    // DD.MM.YYYY
    { re: `^\\d{2}${separator}\\d{2}${separator}\\d{4}$`, build: ([day, month, year]) => new Date(Date.UTC(+year, +month - 1, +day)) },
    // DD.MM.YY is expanded to DD.MM.20YY
    { re: `^\\d{2}${separator}\\d{2}${separator}\\d{2}$`, build: ([day, month, year]) => new Date(Date.UTC(Number('20' + year), +month - 1, +day)) },
    // DD.MM is expanded to DD.MM.YYYY
    { re: `^\\d{2}${separator}\\d{2}$`, build: ([day, month]) => new Date(Date.UTC(currentYear, +month - 1, +day)) },
    // DD. is expanded to DD.MM.YYYY
    { re: `^\\d{2}${separator}$`, build: ([day]) => new Date(Date.UTC(currentYear, currentMonth, +day)) },
    // D. is expanded to DD.MM.YYYY
    { re: `^\\d{1}${separator}$`, build: ([day]) => new Date(Date.UTC(currentYear, currentMonth, +day)) },
    // D.M is expanded to DD.MM.YYYY
    { re: `^\\d{1}${separator}\\d{1}$`, build: ([day, month]) => new Date(Date.UTC(currentYear, +month - 1, +day)) },
    // DD.M is expanded to DD.MM.YYYY
    { re: `^\\d{2}${separator}\\d{1}$`, build: ([day, month]) => new Date(Date.UTC(currentYear, +month - 1, +day)) },
    // D.MM is expanded to DD.MM.YYYY
    { re: `^\\d{1}${separator}\\d{2}$`, build: ([day, month]) => new Date(Date.UTC(currentYear, +month - 1, +day)) }
  ]);
}

// Returns the built date for the first pattern whose (escaped) regex matches dateStr, or null when
// none match. A matched-but-invalid date yields null without trying later patterns, exactly as the
// former per-branch `return isValidDate(date) ? date : null` did.
function firstDatePatternMatch (
  dateStr: string,
  parts: string[],
  patterns: Array<{ re: string; build: (parts: string[]) => Date }>
): Date | null {
  for (const { re, build } of patterns) {
    if (new RegExp(safe(re)).test(dateStr)) {
      const date = build(parts);
      return isValidDate(date) ? date : null;
    }
  }
  return null;
}

function parseDateMdy (dateStr: string): Date | null {
  const separator = findSeparator(dateStr);

  // MM.DD expanded to MM.DD.YYYY
  if (new RegExp(safe(`^\\d{2}${separator}\\d{2}$`)).test(dateStr)) {
    const [month, day] = dateStr.split(separator);
    const date = new Date(Date.UTC(new Date().getFullYear(), +month - 1, +day));
    return isValidDate(date) ? date : null;
  }

  // MM.DD.YY expanded to MM.DD.20YY
  if (new RegExp(safe(`^\\d{2}${separator}\\d{2}${separator}\\d{2}$`)).test(dateStr)) {
    const [month, day, year] = dateStr.split(separator);
    const date = new Date(Date.UTC(Number('20' + year), +month - 1, +day));
    return isValidDate(date) ? date : null;
  }

  // MM.DD.YYYY
  if (new RegExp(safe(`^\\d{2}${separator}\\d{2}${separator}\\d{4}$`)).test(dateStr)) {
    const [month, day, year] = dateStr.split(separator);
    const date = new Date(Date.UTC(+year, +month - 1, +day));
    return isValidDate(date) ? date : null;
  }
  return null;
}

function findSeparator (dateStr: string) {
  let separator = '.';
  if (dateStr.indexOf('/') !== -1) {
    separator = '/';
  } else if (dateStr.indexOf('-') !== -1) {
    separator = '-';
  }
  return separator;
}

function parseDateYmd (dateStr: string): Date | null {
  const separator = findSeparator(dateStr);

  // YYYY.MM.DD
  let regex = safe(`^\\d{4}${separator}\\d{2}${separator}\\d{2}$`);
  if (new RegExp(regex).test(dateStr)) {
    const [year, month, day] = dateStr.split(separator);
    const date = new Date(Date.UTC(+year, +month - 1, +day));
    return isValidDate(date) ? date : null;
  }

  // YY.MM.DD expanded to 20YY.MM.DD
  regex = safe(`^\\d{2}${separator}\\d{2}${separator}\\d{2}$`);
  if (new RegExp(regex).test(dateStr)) {
    const [year, month, day] = dateStr.split(separator);
    const date = new Date(Date.UTC(Number('20' + year), +month - 1, +day));
    return isValidDate(date) ? date : null;
  }

  // MM.DD expanded to YYYY.MM.DD
  regex = safe(`^\\d{2}${separator}\\d{2}$`);
  if (new RegExp(regex).test(dateStr)) {
    const [month, day] = dateStr.split(separator);
    const date = new Date(Date.UTC(new Date().getFullYear(), +month - 1, +day));
    return isValidDate(date) ? date : null;
  }

  return null;
}

// apply escaping of reserved characters '.' and '/' in string that will be used in regex
function safe (regex: string) {
  return regex.replace(/[./]/g, '\\$&');
}


export function formatDateOnly (dateValue: string | Date | null): string {
  if (!dateValue) return '';

  // Hvis det er en string på ISO format (YYYY-MM-DDTHH:mm:ss)
  if (typeof dateValue === 'string' && dateValue.includes('T')) {
    const [datePart] = dateValue.split('T');
    const [year, month, day] = datePart.split('-');
    return `${day}.${month}.${year}`;
  }

  const date = dateValue instanceof Date ? dateValue : getUTCDate(dateValue);
  if (!isValidDate(date)) return String(dateValue);

  const day = date.getUTCDate().toString().padStart(2, '0');
  const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
  const year = date.getUTCFullYear();

  return `${day}.${month}.${year}`;
}

export function formatDate (dateValue: string | Date | null, timeZone?: string): string {
  if (!dateValue) return '';

  // Backend leverer UTC-tid. Vi parser som UTC og konverterer kun ved visning.
  const date = dateValue instanceof Date ? dateValue : getUTCDate(dateValue);
  if (!isValidDate(date)) {
    console.warn('Invalid date', dateValue);
    return String(dateValue);
  }

  const resolvedTimeZone = timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const formattedParts = new Intl.DateTimeFormat('nb-NO', {
    timeZone: resolvedTimeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  }).formatToParts(date);
  const partLookup = Object.fromEntries(formattedParts.map(part => [part.type, part.value]));

  const day = partLookup.day ?? '';
  const month = partLookup.month ?? '';
  const year = partLookup.year ?? '';
  const hours = partLookup.hour ?? '00';
  const minutes = partLookup.minute ?? '00';
  let result = `${day}.${month}.${year}`;

  const hasExplicitTime = typeof dateValue === 'string' ? dateValue.includes('T') : (date.getUTCHours() || date.getUTCMinutes() || date.getUTCSeconds() || date.getUTCMilliseconds());
  const hasTime = Boolean(hasExplicitTime);
  if (hasTime) {
    result += ` ${hours}:${minutes}`;
  }

  return result;
}

export function getUTCDate (dateValue: string): Date {
  const parsed = tryGetUTCDate(dateValue);
  if (parsed) return parsed;
  throw new Error('Invalid date value');
}

/** Som `getUTCDate`, men returnerer `null` for uparsbare verdier i stedet for å kaste. */
export function tryGetUTCDate (dateValue: string): Date | null {
  if (dateValue.includes('T')) {
    const hasOffset = /([zZ]|[+-]\d{2}:\d{2})$/.test(dateValue);
    const normalized = hasOffset ? dateValue : `${dateValue}Z`;
    const parsed = new Date(normalized);
    return isValidDate(parsed) ? parsed : null;
  }
  // Ren dato (åååå-mm-dd) fra dato-kolonner tolkes som UTC-midnatt.
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
    const parsed = new Date(`${dateValue}T00:00:00Z`);
    return isValidDate(parsed) ? parsed : null;
  }
  return null;
}

function isValidDate (date: Date): boolean {
  return !Number.isNaN(date.getTime());
}
